import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import { MessageSquarePlus, Inbox } from "lucide-react";

// Phase B3 — colours match the brief: Open=blue, Awaiting support=orange,
// Awaiting customer=warm yellow, Resolved=green, Closed=muted.
export const STATUS_STYLES = {
  "Open":              { bg: "bg-blue-100 text-blue-700 border-blue-200" },
  "Awaiting support":  { bg: "bg-orange-100 text-orange-700 border-orange-200" },
  "Awaiting customer": { bg: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  "Resolved":          { bg: "bg-green-100 text-green-700 border-green-200" },
  "Closed":            { bg: "bg-navy-100 text-navy-500 border-navy-200" },
};

export const PRIORITY_STYLES = {
  "Low":    "bg-navy-50 text-navy-500 border-navy-200",
  "Normal": "bg-navy-100 text-navy-700 border-navy-200",
  "High":   "bg-orange-50 text-orange-700 border-orange-200",
  "Urgent": "bg-red-50 text-red-700 border-red-200",
};

export function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES["Open"];
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.bg}`} data-testid={`status-pill-${status}`}>
      {status}
    </span>
  );
}

export function PriorityPill({ priority }) {
  const c = PRIORITY_STYLES[priority] || PRIORITY_STYLES["Normal"];
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c}`}>
      {priority}
    </span>
  );
}

export default function HelpTickets() {
  const { formatDateTime } = useLocale();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/support/tickets")
      .then(({ data }) => setTickets(data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell title="My tickets">
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="tickets-heading">My tickets</h1>
            <p className="text-sm text-navy-500 mt-0.5">Track every support conversation for your organisation.</p>
          </div>
          <Link
            to="/help/tickets/new"
            className="btn-accent inline-flex items-center gap-1.5"
            data-testid="tickets-new-cta"
          >
            <MessageSquarePlus className="w-4 h-4" /> Submit a ticket
          </Link>
        </div>
        {loading ? (
          <div className="text-xs text-navy-400">Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="eb-card p-12 text-center" data-testid="tickets-empty">
            <Inbox className="w-10 h-10 text-navy-300 mx-auto mb-3" />
            <p className="text-sm text-navy-500">No tickets yet. Need help? Submit a ticket.</p>
            <Link to="/help/tickets/new" className="btn-accent mt-4 inline-flex" data-testid="tickets-empty-cta">
              <MessageSquarePlus className="w-4 h-4" /> Submit a ticket
            </Link>
          </div>
        ) : (
          <div className="eb-card p-0 overflow-hidden" data-testid="tickets-list">
            <table className="w-full text-sm">
              <thead className="bg-navy-50/40 text-[11px] uppercase tracking-wider text-navy-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Subject</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Priority</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} className="border-t border-navy-100" data-testid={`ticket-row-${t.id}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/help/tickets/${t.id}`} className="font-medium text-navy-900 dark:text-white hover:text-orange-600">
                        {t.subject}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-navy-500">{t.category}</td>
                    <td className="px-4 py-2.5"><PriorityPill priority={t.priority} /></td>
                    <td className="px-4 py-2.5"><StatusPill status={t.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-navy-500">{formatDateTime(t.last_message_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
